/**
 * Arbitrage Calculation Utilities
 *
 * Calculates spreads and profits for futures-spot arbitrage opportunities
 * with exchange fees factored in.
 *
 * Key Concepts:
 * - Long Arbitrage: Buy spot (ask price), sell futures (bid price)
 * - Short Arbitrage: Sell spot (bid price), buy futures (ask price)
 * - Fees: Applied to both entry and exit legs
 */

/**
 * Exchange fee structure (as decimal, e.g., 0.001 = 0.1%)
 */
export const EXCHANGE_FEES = {
  binanceSpot: 0.001,      // 0.1% per trade
  binanceFutures: 0.0004,  // 0.04% taker fee
  upbit: 0.0005,           // 0.05% per trade
  bithumb: 0.0025,         // 0.25% per trade
} as const;

/**
 * Get fee for a specific exchange
 */
export function getExchangeFee(exchange: string): number {
  const normalizedExchange = exchange.toLowerCase().replace(/\s+/g, '');

  if (normalizedExchange.includes('binance')) {
    if (normalizedExchange.includes('futures')) {
      return EXCHANGE_FEES.binanceFutures;
    }
    return EXCHANGE_FEES.binanceSpot;
  }

  if (normalizedExchange.includes('upbit')) {
    return EXCHANGE_FEES.upbit;
  }

  if (normalizedExchange.includes('bithumb')) {
    return EXCHANGE_FEES.bithumb;
  }

  // Default to Binance spot fee for unknown exchanges
  return EXCHANGE_FEES.binanceSpot;
}

/**
 * Fee breakdown for a trade pair
 */
export interface FeeBreakdown {
  spotFee: number;         // Absolute fee amount for spot trade
  futuresFee: number;      // Absolute fee amount for futures trade
  totalFee: number;        // Total fees (absolute)
  spotFeeRate: number;     // Spot fee rate (e.g., 0.001 = 0.1%)
  futuresFeeRate: number;  // Futures fee rate
  totalFeePercentage: number; // Total fees as percentage of entry price
}

/**
 * Calculate fees for a trade pair
 *
 * @param spotPrice - Spot entry/exit price
 * @param futuresPrice - Futures entry/exit price
 * @param spotExchange - Spot exchange name
 * @param futuresExchange - Futures exchange name (always "Binance Futures")
 * @returns Fee breakdown
 */
export function calculateFees(
  spotPrice: number,
  futuresPrice: number,
  spotExchange: string,
  futuresExchange: string = 'Binance Futures'
): FeeBreakdown {
  const spotFeeRate = getExchangeFee(spotExchange);
  const futuresFeeRate = getExchangeFee(futuresExchange);

  const spotFee = spotPrice * spotFeeRate;
  const futuresFee = futuresPrice * futuresFeeRate;
  const totalFee = spotFee + futuresFee;

  // Calculate total fee percentage based on entry price
  const entryPrice = Math.min(spotPrice, futuresPrice);
  const totalFeePercentage = (totalFee / entryPrice) * 100;

  return {
    spotFee,
    futuresFee,
    totalFee,
    spotFeeRate,
    futuresFeeRate,
    totalFeePercentage,
  };
}

/**
 * Arbitrage opportunity result
 */
export interface ArbitrageOpportunity {
  entryPrice: number;           // Entry price
  exitPrice: number;            // Exit price
  grossSpread: number;          // Spread before fees (absolute)
  grossSpreadPercentage: number; // Spread before fees (%)
  fees: FeeBreakdown;           // Fee breakdown
  netProfit: number;            // Profit after fees (absolute)
  netProfitPercentage: number;  // Profit after fees (%)
  isProfitable: boolean;        // Whether profitable after fees (>0.5%)
}

/**
 * Calculate long arbitrage opportunity
 *
 * Long = Buy spot at ask price, sell futures at bid price
 *
 * @param spotAsk - Spot ask price (buy price)
 * @param futuresBid - Futures bid price (sell price)
 * @param spotExchange - Spot exchange name
 * @param futuresExchange - Futures exchange name
 * @returns Arbitrage opportunity details
 */
export function calculateLongArbitrage(
  spotAsk: number,
  futuresBid: number,
  spotExchange: string,
  futuresExchange: string = 'Binance Futures'
): ArbitrageOpportunity {
  // Entry: Buy spot at ask
  const entryPrice = spotAsk;

  // Exit: Sell futures at bid
  const exitPrice = futuresBid;

  // Gross spread (before fees)
  const grossSpread = exitPrice - entryPrice;
  const grossSpreadPercentage = (grossSpread / entryPrice) * 100;

  // Calculate fees
  const fees = calculateFees(spotAsk, futuresBid, spotExchange, futuresExchange);

  // Net profit (after fees)
  const netProfit = grossSpread - fees.totalFee;
  const netProfitPercentage = (netProfit / entryPrice) * 100;

  // Profitable if net profit > 0.5%
  const isProfitable = netProfitPercentage > 0.5;

  return {
    entryPrice,
    exitPrice,
    grossSpread,
    grossSpreadPercentage,
    fees,
    netProfit,
    netProfitPercentage,
    isProfitable,
  };
}

/**
 * Calculate short arbitrage opportunity
 *
 * Short = Sell spot at bid price, buy futures at ask price
 *
 * @param spotBid - Spot bid price (sell price)
 * @param futuresAsk - Futures ask price (buy price)
 * @param spotExchange - Spot exchange name
 * @param futuresExchange - Futures exchange name
 * @returns Arbitrage opportunity details
 */
export function calculateShortArbitrage(
  spotBid: number,
  futuresAsk: number,
  spotExchange: string,
  futuresExchange: string = 'Binance Futures'
): ArbitrageOpportunity {
  // Entry: Sell spot at bid
  const entryPrice = spotBid;

  // Exit: Buy futures at ask
  const exitPrice = futuresAsk;

  // Gross spread (before fees)
  const grossSpread = entryPrice - exitPrice;
  const grossSpreadPercentage = (grossSpread / entryPrice) * 100;

  // Calculate fees
  const fees = calculateFees(spotBid, futuresAsk, spotExchange, futuresExchange);

  // Net profit (after fees)
  const netProfit = grossSpread - fees.totalFee;
  const netProfitPercentage = (netProfit / entryPrice) * 100;

  // Profitable if net profit > 0.5%
  const isProfitable = netProfitPercentage > 0.5;

  return {
    entryPrice,
    exitPrice,
    grossSpread,
    grossSpreadPercentage,
    fees,
    netProfit,
    netProfitPercentage,
    isProfitable,
  };
}

/**
 * Format currency value with appropriate decimal places
 */
export function formatCurrency(value: number, quoteAsset: string = 'USDT'): string {
  if (quoteAsset === 'KRW') {
    return `₩${Math.abs(value).toFixed(0)}`;
  }
  return `$${Math.abs(value).toFixed(2)}`;
}

/**
 * Format percentage value
 */
export function formatPercentage(value: number): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
}

/**
 * Get color for profit/loss display
 */
export function getProfitColor(profitPercentage: number): 'success' | 'warning' | 'error' {
  if (profitPercentage > 0.5) return 'success';    // >0.5% = profitable (green)
  if (profitPercentage > 0) return 'warning';      // 0-0.5% = marginal (yellow)
  return 'error';                                  // <0% = unprofitable (red)
}
